import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { InvoiceDetailModel } from '@app/core/models/invoices/invoice-detail.model';
import { Subject, Observable } from 'rxjs';
import { WorkflowService } from '@app/core/services/workflow.service';
import { takeUntil } from 'rxjs/operators';
import { MultiSelectModel } from '@app/shared/models/multi-select.model';
import { WorkflowModel } from '@app/core/models/workflows/workflow.model';
import * as _ from 'lodash';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { ModalHelper } from '@app/shared/models/modal-helper.model';
import { InvoiceService } from '@app/core/services/invoice.service';
import { InvoiceVoucherProcessModel } from '@app/core/models/invoices/vouchers/invoice-voucher-process.model';
import { InvoiceVoucherFormComponent } from '@app/features/invoices/actions/voucher/invoice-voucher-form/invoice-voucher-form.component';
import { GroupService } from '@app/core/services/group.service';
import { GroupModel } from '@app/core/models/groups/group.model';

@Component({
  selector: 'app-invoice-voucher-process',
  templateUrl: './invoice-voucher-process.component.html',
  styleUrls: ['./invoice-voucher-process.component.scss'],
})
export class InvoiceVoucherProcessComponent implements OnInit, OnDestroy {
  invoiceDetailModel: InvoiceDetailModel;
  submitted = true;
  unsubscribe$: Subject<boolean> = new Subject<boolean>();
  dropDownSettingsWorkflow: any;
  dropDownSelectedWorkflow: number;
  dropDownDataWorkflow = [];
  selectedWorkflow: WorkflowModel[] = [];
  workflowForm: UntypedFormGroup;
  singleWorkflow: WorkflowModel;

  @ViewChild(InvoiceVoucherFormComponent)
  invoiceVoucherFormComponent: InvoiceVoucherFormComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private workflowService: WorkflowService,
    private formBuilder: UntypedFormBuilder,
    private invoiceService: InvoiceService,
    private groupService: GroupService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.buildFormControls();

    this.route.data.subscribe((data) => {
      this.invoiceDetailModel = data.invoice;
      this.setDropDownSettingsWorkflow();

      this.groupService
        .getGroupById(this.invoiceDetailModel.CurrentAssignmentGroupId)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((group: GroupModel) => {
          if (_.isNil(this.invoiceDetailModel.VoucherAnalyticalSection)) {
            this.invoiceDetailModel.VoucherAnalyticalSection =
              group.VoucherAnalyticalSection;
            this.invoiceVoucherFormComponent.InvoiceDetailForm.patchValue({
              VoucherAnalyticalSection: group.VoucherAnalyticalSection,
            });
          }
          if (_.isNil(this.invoiceDetailModel.VoucherComment)) {
            this.invoiceDetailModel.VoucherComment = group.VoucherComment;
            this.invoiceVoucherFormComponent.InvoiceDetailForm.patchValue({
              VoucherComment: group.VoucherComment,
            });
          }
          if (_.isNil(this.invoiceDetailModel.VoucherDepartment)) {
            this.invoiceDetailModel.VoucherDepartment = group.VoucherDepartment;
            this.invoiceVoucherFormComponent.InvoiceDetailForm.patchValue({
              VoucherDepartment: group.VoucherDepartment,
            });
          }
        });
    });
  }

  goBackToInvoiceList() {
    this.router.navigate(['/invoices/dispatched']);
  }

  get IsSingleWorkflow(): boolean {
    return !_.isNil(this.singleWorkflow);
  }

  get IsWorkflowFormValid(): boolean {
    return this.IsSingleWorkflow || this.workflowForm.valid;
  }

  get AreFormsValid(): boolean {
    return (
      this.IsWorkflowFormValid &&
      this.invoiceVoucherFormComponent?.InvoiceDetailForm?.valid
    );
  }

  setDropDownSettingsWorkflow() {
    this.workflowService
      .getOperationalGroupWorkflows(
        this.invoiceDetailModel.CurrentAssignmentGroupId
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((workflows: WorkflowModel[]) => {
        this.dropDownDataWorkflow = workflows;
        if (workflows.length == 1) {
          this.singleWorkflow = workflows[0];
          this.selectedWorkflow = [this.singleWorkflow];
        } else if (!_.isNil(this.invoiceDetailModel.Workflow)) {
          this.selectedWorkflow = workflows.filter(
            (x) => x.Id == this.invoiceDetailModel.Workflow?.Id
          );
        }
      });
    this.dropDownSettingsWorkflow = new MultiSelectModel().getSettings(
      'Id',
      'Name',
      true,
      true
    );
  }

  buildFormControls() {
    this.workflowForm = this.formBuilder.group({
      Workflow: new UntypedFormControl(
        {
          disabled: this.IsSingleWorkflow,
          value: [this.selectedWorkflow],
        },
        [Validators.required]
      ),
    });
  }

  onSubmit() {
    if (this.AreFormsValid) {
      this.onUpdate().subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.invoiceService
            .removeFromWaitingStatus(this.invoiceDetailModel.Id)
            .subscribe(() => {
              this.processVoucher();
            });
        }
      });
    }
  }

  onUpdate(): Observable<any> {
    let selectedWorkflow = this.selectedWorkflow[0];
    const confimModal = new ModalHelper().getConfirmationConfig(
      `Êtes-vous sûr de vouloir marquer ce bon à payer comme traité et l'affecter au circuit de validation '${selectedWorkflow.Name}' ?`
    );
    const dialog = this.dialog.open(ModalComponent, confimModal);

    return dialog.afterClosed().pipe(takeUntil(this.unsubscribe$));
  }

  processVoucher() {
    const invoiceDetailForm =
      this.invoiceVoucherFormComponent.InvoiceDetailForm.controls;
    const invoiceVoucherProcessModel: InvoiceVoucherProcessModel =
      new InvoiceVoucherProcessModel();
    invoiceVoucherProcessModel.Id = this.invoiceDetailModel.Id;
    invoiceVoucherProcessModel.VoucherAnalyticalCode =
      invoiceDetailForm.VoucherAnalyticalCode.value;
    invoiceVoucherProcessModel.VoucherAnalyticalSection =
      invoiceDetailForm.VoucherAnalyticalSection.value;
    invoiceVoucherProcessModel.VoucherComment =
      invoiceDetailForm.VoucherComment.value;
    invoiceVoucherProcessModel.VoucherDepartment =
      invoiceDetailForm.VoucherDepartment.value;
    invoiceVoucherProcessModel.WorkflowId = this.IsSingleWorkflow
      ? this.singleWorkflow.Id
      : this.selectedWorkflow[0]?.Id;
    this.invoiceService
      .processVoucher(invoiceVoucherProcessModel.Id, invoiceVoucherProcessModel)
      .subscribe(() => {
        this.goBackToInvoiceList();
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }
}
